import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { showError } from './ErrorModal';
import { seattleWallToUtcDate, toAppDateString } from '../utils/dateUtils';
import {
  breakTypesForLocation,
  formatBreakTypeLabel,
} from '../utils/breakPay';

function toLocalDateString(date = new Date()) {
  return toAppDateString(date);
}

/** Shift a Seattle YYYY-MM-DD by whole calendar days. */
function addAppDays(dateStr, delta) {
  const noon = seattleWallToUtcDate(String(dateStr).slice(0, 10), '12:00:00');
  return toAppDateString(new Date(noon.getTime() + delta * 24 * 60 * 60 * 1000));
}

/**
 * Build a Seattle wall-clock instant. When end time is earlier than start on the
 * same calendar date, treat end as the next Seattle day (overnight punch).
 */
function seattleRangeFromDateAndTimes(dateStr, startTime, endTime) {
  const startAt = seattleWallToUtcDate(dateStr, startTime);
  let endAt = seattleWallToUtcDate(dateStr, endTime);
  if (endAt <= startAt) {
    endAt = seattleWallToUtcDate(addAppDays(dateStr, 1), endTime);
  }
  return { startAt, endAt };
}

function emptyForm() {
  return {
    teamMemberId: '',
    locationId: '',
    jobId: '',
    date: toLocalDateString(),
    clockIn: '',
    clockOut: '',
    breaks: [],
    tipEligible: true,
    is_shift_lead_day: false,
  };
}

function CreateTimecardModal({
  isOpen,
  onClose,
  onCreated,
  teamMembers,
  selectableTeamMembers,
  breakTypes,
  locations,
  loadingFormData,
  ownTeamMemberIds,
  currentUserEmployeeId = null,
  canViewWages,
  canViewShiftLeadDayMarks = false,
  canEditShiftLeadDayMarks = false,
  isMarkedShiftLeadDay,
}) {
  const [formData, setFormData] = useState(() => emptyForm());
  const [submitting, setSubmitting] = useState(false);
  const leadDaySyncKeyRef = useRef('');

  // Reset when the modal opens so prior selections don't leak across creates
  useEffect(() => {
    if (!isOpen) {
      leadDaySyncKeyRef.current = '';
      return;
    }
    setFormData(emptyForm());
    leadDaySyncKeyRef.current = '';
  }, [isOpen]);

  const selectedMember = useMemo(
    () => teamMembers.find((tm) => tm.id === formData.teamMemberId) || null,
    [teamMembers, formData.teamMemberId]
  );

  const assignedLocations = selectedMember?.assignedLocations || null;
  const memberJobs = Array.isArray(selectedMember?.jobs) ? selectedMember.jobs : [];

  const availableLocations = useMemo(() => {
    if (!formData.teamMemberId) return [];
    if (!Array.isArray(locations) || locations.length === 0) return [];
    const assignmentType = assignedLocations?.assignmentType || '';
    const ids = Array.isArray(assignedLocations?.locationIds)
      ? assignedLocations.locationIds.map(String)
      : [];
    if (
      assignmentType === 'ALL_CURRENT_AND_FUTURE_LOCATIONS' ||
      !assignedLocations
    ) {
      return locations;
    }
    if (ids.length === 0) return [];
    return locations.filter((loc) => ids.includes(String(loc.id)));
  }, [formData.teamMemberId, locations, assignedLocations]);

  // Auto-select when the member has exactly one allowed location
  useEffect(() => {
    if (!isOpen || !formData.teamMemberId) return;
    if (availableLocations.length !== 1) return;
    const onlyId = availableLocations[0].id;
    setFormData((prev) =>
      prev.locationId === onlyId ? prev : { ...prev, locationId: onlyId, jobId: '' }
    );
  }, [isOpen, formData.teamMemberId, availableLocations]);

  // Prefill shift-lead day mark when employee/date/location identity changes.
  useEffect(() => {
    if (!isOpen || !canViewShiftLeadDayMarks) return;
    if (typeof isMarkedShiftLeadDay !== 'function') return;
    const key = `${selectedMember?.local_id || ''}|${formData.date || ''}|${formData.locationId || ''}`;
    if (leadDaySyncKeyRef.current === key) return;
    leadDaySyncKeyRef.current = key;
    const marked = isMarkedShiftLeadDay(
      selectedMember?.local_id,
      formData.date,
      formData.locationId
    );
    setFormData((prev) =>
      prev.is_shift_lead_day === marked
        ? prev
        : { ...prev, is_shift_lead_day: marked }
    );
  }, [
    isOpen,
    canViewShiftLeadDayMarks,
    isMarkedShiftLeadDay,
    selectedMember?.local_id,
    formData.date,
    formData.locationId,
  ]);

  // Jobs unlock only after a location is chosen (data is already on the member)
  const jobs = formData.locationId ? memberJobs : [];

  const locationBreakTypes = useMemo(
    () =>
      breakTypesForLocation(
        breakTypes,
        formData.locationId,
        (formData.breaks || []).map((b) => b.breakTypeId)
      ),
    [breakTypes, formData.locationId, formData.breaks]
  );

  const formatJobOptionLabel = (job) => {
    const jobTitle = job.title || 'Untitled job';
    if (!canViewWages || !job.wage) {
      return jobTitle;
    }

    const parsedWage = Number.parseFloat(job.wage);
    if (Number.isNaN(parsedWage)) {
      return `${jobTitle} - ${job.wage}`;
    }

    return `${jobTitle} - $${parsedWage.toFixed(2)}/hr`;
  };

  const addBreak = () => {
    setFormData((prev) => ({
      ...prev,
      breaks: [...prev.breaks, { startAt: '', endAt: '', breakTypeId: '', name: '', isPaid: false, expectedDuration: '' }],
    }));
  };

  const removeBreak = (index) => {
    setFormData((prev) => ({
      ...prev,
      breaks: prev.breaks.filter((_, i) => i !== index),
    }));
  };

  const updateBreak = (index, field, value) => {
    setFormData((prev) => {
      const newBreaks = [...prev.breaks];
      newBreaks[index] = { ...newBreaks[index], [field]: value };

      if (field === 'breakTypeId' && value) {
        const selectedBreakType = breakTypes.find((bt) => bt.id === value);
        if (selectedBreakType) {
          newBreaks[index].name = selectedBreakType.name;
          newBreaks[index].isPaid = selectedBreakType.isPaid;
          newBreaks[index].expectedDuration = selectedBreakType.expectedDuration || '';
        }
      }

      return { ...prev, breaks: newBreaks };
    });
  };

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const handleTeamMemberChange = (teamMemberId) => {
    setFormData((prev) => ({
      ...prev,
      teamMemberId,
      locationId: '',
      jobId: '',
      tipEligible: true,
    }));
  };

  const handleLocationChange = (locationId) => {
    setFormData((prev) => ({
      ...prev,
      locationId,
      jobId: '',
      tipEligible: true,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.teamMemberId || !formData.locationId || !formData.date || !formData.clockIn || !formData.clockOut) {
      showError('Please fill in all required fields');
      return;
    }

    const selectedMember = teamMembers.find(
      (tm) => String(tm.id) === String(formData.teamMemberId),
    );
    const creatingForSelf =
      ownTeamMemberIds.has(String(formData.teamMemberId)) ||
      (currentUserEmployeeId != null &&
        selectedMember?.local_id != null &&
        Number(selectedMember.local_id) === Number(currentUserEmployeeId));
    if (creatingForSelf) {
      showError('You cannot create a timecard for yourself.');
      return;
    }

    for (const b of formData.breaks) {
      const hasAny = !!(b.breakTypeId || b.startAt || b.endAt);
      if (!hasAny) continue;
      if (!b.breakTypeId || !b.startAt || !b.endAt) {
        showError('Each break needs a type, start time, and end time');
        return;
      }
    }

    const { startAt, endAt } = seattleRangeFromDateAndTimes(
      formData.date,
      formData.clockIn,
      formData.clockOut
    );

    if (startAt >= endAt) {
      showError('Clock out time must be after clock in time');
      return;
    }

    for (const b of formData.breaks) {
      if (!b.breakTypeId || !b.startAt || !b.endAt) continue;
      const { startAt: breakStart, endAt: breakEnd } = seattleRangeFromDateAndTimes(
        formData.date,
        b.startAt,
        b.endAt
      );
      if (breakStart >= breakEnd) {
        showError('Break end time must be after break start time');
        return;
      }
      if (breakStart < startAt || breakEnd > endAt) {
        showError('Breaks must fall within the clock-in/out times');
        return;
      }
    }

    try {
      setSubmitting(true);
      const selectedJob = jobs.find(
        (j) =>
          (j.id && j.id === formData.jobId) ||
          (j.title && j.title === formData.jobId) ||
          (j.id === null && j.title === formData.jobId)
      );

      const finalJobTitle = selectedJob ? selectedJob.title : formData.jobId || null;
      const finalJobWage = selectedJob && selectedJob.wage ? selectedJob.wage : null;

      const timecardData = {
        teamMemberId: formData.teamMemberId,
        locationId: formData.locationId,
        jobId: null,
        jobTitle: finalJobTitle,
        jobWage: finalJobWage,
        tipEligible: formData.tipEligible !== undefined ? formData.tipEligible : true,
        startAt: startAt.toISOString(),
        endAt: endAt.toISOString(),
        breaks: formData.breaks
          .filter((b) => b.startAt && b.endAt && b.breakTypeId)
          .map((b) => {
            const range = seattleRangeFromDateAndTimes(formData.date, b.startAt, b.endAt);
            return {
              startAt: range.startAt.toISOString(),
              endAt: range.endAt.toISOString(),
              breakTypeId: b.breakTypeId,
              name: b.name || 'Break',
              isPaid: b.isPaid !== undefined ? b.isPaid : false,
              expectedDuration: b.expectedDuration || undefined,
            };
          }),
      };

      await axios.post('/api/square/timecards', timecardData);

      if (
        canEditShiftLeadDayMarks &&
        selectedMember?.local_id != null &&
        formData.locationId &&
        formData.date
      ) {
        const desiredMark = !!formData.is_shift_lead_day;
        const knownMark =
          typeof isMarkedShiftLeadDay === 'function' &&
          isMarkedShiftLeadDay(
            selectedMember.local_id,
            formData.date,
            formData.locationId
          );
        if (desiredMark !== !!knownMark) {
          try {
            await axios.put('/api/schedules/shift-lead-day-assignments', {
              employee_id: selectedMember.local_id,
              assignment_date: String(formData.date).slice(0, 10),
              location_id: formData.locationId,
              assigned: desiredMark,
            });
          } catch (markError) {
            console.error('Error updating shift-lead day mark:', markError);
            showError(
              markError.response?.data?.error ||
                'Timecard created, but failed to update shift-lead day mark'
            );
          }
        }
      }

      setFormData(emptyForm());
      setSubmitting(false);
      onClose();
      // Refresh list after close so the modal never waits on the full refetch
      onCreated?.();
    } catch (error) {
      console.error('Error creating timecard:', error);
      const errorMessage = error.response?.data?.error || 'Failed to create timecard';
      showError(errorMessage);
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const locationDisabled = !formData.teamMemberId;
  const jobsDisabled = !formData.teamMemberId || !formData.locationId;

  return createPortal(
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content timecard-modal timecard-modal-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Create Timecard</h3>
          <button className="modal-close" onClick={handleClose} disabled={submitting}>
            ×
          </button>
        </div>
        <form onSubmit={handleSubmit} className="timecard-modal-form">
          <div className="timecard-modal-body">
            <div style={{ marginBottom: '1rem' }}>
              <label htmlFor="team-member" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Team Member <span style={{ color: '#d32f2f' }}>*</span>
              </label>
              <select
                id="team-member"
                value={formData.teamMemberId}
                onChange={(e) => handleTeamMemberChange(e.target.value)}
                required
                disabled={loadingFormData && teamMembers.length === 0}
                style={{ width: '100%', padding: '0.5rem', fontSize: '0.9375rem' }}
              >
                <option value="">
                  {loadingFormData && teamMembers.length === 0
                    ? 'Loading team members…'
                    : 'Select a team member'}
                </option>
                {selectableTeamMembers.map((tm) => (
                  <option key={tm.id} value={tm.id}>
                    {tm.name}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label htmlFor="create-location" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Location <span style={{ color: '#d32f2f' }}>*</span>
              </label>
              <select
                id="create-location"
                value={formData.locationId}
                onChange={(e) => handleLocationChange(e.target.value)}
                required
                disabled={locationDisabled}
                style={{ width: '100%', padding: '0.5rem', fontSize: '0.9375rem' }}
              >
                <option value="">
                  {!formData.teamMemberId
                    ? 'Select a team member first'
                    : availableLocations.length === 0
                      ? 'No assigned locations'
                      : 'Select a location'}
                </option>
                {availableLocations.map((loc) => (
                  <option key={loc.id} value={loc.id}>
                    {loc.name}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label htmlFor="job" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Job Title
              </label>
              <select
                id="job"
                value={formData.jobId}
                onChange={(e) => {
                  const nextJobId = e.target.value;
                  const selectedJob = jobs.find((job) => (job.id || job.title) === nextJobId);
                  setFormData((prev) => ({
                    ...prev,
                    jobId: nextJobId,
                    tipEligible: selectedJob ? !!selectedJob.tipEligible : true,
                  }));
                }}
                disabled={jobsDisabled}
                style={{ width: '100%', padding: '0.5rem', fontSize: '0.9375rem' }}
              >
                <option value="">
                  {!formData.teamMemberId
                    ? 'Select a team member first'
                    : !formData.locationId
                      ? 'Select a location first'
                      : 'No job assignment'}
                </option>
                {jobs.map((job) => (
                  <option key={job.id || job.title} value={job.id || job.title}>
                    {formatJobOptionLabel(job)}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.tipEligible}
                  onChange={(e) => setFormData((prev) => ({ ...prev, tipEligible: e.target.checked }))}
                  style={{ cursor: 'pointer' }}
                />
                <span style={{ fontWeight: '500' }}>Tip Eligible</span>
              </label>
            </div>

            {canViewShiftLeadDayMarks && (
              <div style={{ marginBottom: '1rem' }}>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.5rem',
                    cursor: canEditShiftLeadDayMarks ? 'pointer' : 'default',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={!!formData.is_shift_lead_day}
                    disabled={!canEditShiftLeadDayMarks || !formData.teamMemberId || !formData.locationId}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        is_shift_lead_day: e.target.checked,
                      }))
                    }
                    style={{ cursor: canEditShiftLeadDayMarks ? 'pointer' : 'not-allowed', marginTop: '0.2rem' }}
                  />
                  <span>
                    <span style={{ fontWeight: '500' }}>Mark as shift lead this day</span>
                    <span
                      style={{
                        display: 'block',
                        fontSize: '0.8125rem',
                        fontWeight: 400,
                        color: '#6b7280',
                        marginTop: '0.15rem',
                      }}
                    >
                      {canEditShiftLeadDayMarks
                        ? 'Grants timecard access for this date and location even without a shift-lead schedule role.'
                        : 'You can view this mark but do not have permission to change it.'}
                    </span>
                  </span>
                </label>
              </div>
            )}

            <div style={{ marginBottom: '1rem' }}>
              <label htmlFor="date" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Date <span style={{ color: '#d32f2f' }}>*</span>
              </label>
              <input
                id="date"
                type="date"
                value={formData.date}
                onChange={(e) => setFormData((prev) => ({ ...prev, date: e.target.value }))}
                required
                style={{ width: '100%', padding: '0.5rem', fontSize: '0.9375rem' }}
              />
            </div>

            <div style={{ marginBottom: '1rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <label htmlFor="clock-in" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Clock In <span style={{ color: '#d32f2f' }}>*</span>
                </label>
                <input
                  id="clock-in"
                  type="time"
                  value={formData.clockIn}
                  onChange={(e) => setFormData((prev) => ({ ...prev, clockIn: e.target.value }))}
                  required
                  style={{ width: '100%', padding: '0.5rem', fontSize: '0.9375rem' }}
                />
              </div>
              <div>
                <label htmlFor="clock-out" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Clock Out <span style={{ color: '#d32f2f' }}>*</span>
                </label>
                <input
                  id="clock-out"
                  type="time"
                  value={formData.clockOut}
                  onChange={(e) => setFormData((prev) => ({ ...prev, clockOut: e.target.value }))}
                  required
                  style={{ width: '100%', padding: '0.5rem', fontSize: '0.9375rem' }}
                />
              </div>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <div className="timecard-breaks-header" style={{ marginBottom: '0.5rem' }}>
                <label className="timecard-section-title">Breaks</label>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm timecard-add-break-btn"
                  onClick={addBreak}
                >
                  Add Break
                </button>
              </div>
              <p className="timecard-break-hint">
                Paid rest breaks use Square&apos;s allotted paid time (usually 10 minutes); extra
                time is unpaid.
              </p>
              {formData.breaks.length === 0 ? (
                <p style={{ color: '#666', fontSize: '0.875rem', fontStyle: 'italic' }}>No breaks added</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {formData.breaks.map((breakItem, index) => (
                    <div key={index} style={{ border: '1px solid #ddd', padding: '0.75rem', borderRadius: '4px' }}>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '0.5rem',
                        }}
                      >
                        <label style={{ fontSize: '0.875rem', fontWeight: '500' }}>Break {index + 1}</label>
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={() => removeBreak(index)}
                        >
                          Remove
                        </button>
                      </div>
                      <div style={{ marginBottom: '0.5rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem' }}>
                          Break Type <span style={{ color: '#d32f2f' }}>*</span>
                        </label>
                        <select
                          value={breakItem.breakTypeId || ''}
                          onChange={(e) => updateBreak(index, 'breakTypeId', e.target.value)}
                          required
                          style={{ width: '100%', padding: '0.375rem', fontSize: '0.875rem' }}
                        >
                          <option value="">Select a break type</option>
                          {locationBreakTypes.map((bt) => (
                            <option key={bt.id} value={bt.id}>
                              {formatBreakTypeLabel(bt)}
                            </option>
                          ))}
                        </select>
                        {locationBreakTypes.length === 0 && (
                          <p
                            style={{
                              fontSize: '0.75rem',
                              color: '#666',
                              marginTop: '0.25rem',
                              fontStyle: 'italic',
                            }}
                          >
                            {formData.locationId
                              ? 'No Square break types for this location. Configure them in Square first.'
                              : 'Choose a location to see Square break types for that store.'}
                          </p>
                        )}
                      </div>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '1fr 1fr',
                          gap: '0.5rem',
                          marginBottom: '0.5rem',
                        }}
                      >
                        <div>
                          <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem' }}>
                            Start Time
                          </label>
                          <input
                            type="time"
                            value={breakItem.startAt || ''}
                            onChange={(e) => updateBreak(index, 'startAt', e.target.value)}
                            style={{ width: '100%', padding: '0.375rem', fontSize: '0.875rem' }}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem' }}>
                            End Time
                          </label>
                          <input
                            type="time"
                            value={breakItem.endAt || ''}
                            onChange={(e) => updateBreak(index, 'endAt', e.target.value)}
                            style={{ width: '100%', padding: '0.375rem', fontSize: '0.875rem' }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="modal-actions timecard-modal-actions">

            <button type="button" className="btn btn-secondary" onClick={handleClose} disabled={submitting}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitting || (loadingFormData && teamMembers.length === 0)}
            >
              {submitting ? 'Creating...' : 'Create Timecard'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

export default React.memo(CreateTimecardModal);
